import { useMutation } from '@tanstack/react-query';

import type { MutationConfig } from '@/lib/react-query';
import { api, extractData } from '@/lib/treaty';

import type {
  TYPE_DESIRED_COLOR_OPTIONS,
  TYPE_MATERIAL_OPTIONS,
} from '../constants/colors-and-materials';
import {
  type GoodWeatherDaylightValue,
  type GoodWeatherSeasonValue,
  type ROOM_MODE_OPTION_VALUE,
  ROOM_MODE_OPTIONS,
} from '../constants/room-mode';
import type { TYPE_DESIRED_STYLE_OPTIONS } from '../constants/room-style';
import type { TYPE_ROOM_TYPE_OPTIONS } from '../constants/room-type';

type RenderImageOptions = {
  mutationConfig?: MutationConfig<typeof renderImage>;
};

type RenderImageType = {
  image: File;
  roomtype?: TYPE_ROOM_TYPE_OPTIONS;
  style?: TYPE_DESIRED_STYLE_OPTIONS;
  colors?: TYPE_DESIRED_COLOR_OPTIONS[];
  materials?: TYPE_MATERIAL_OPTIONS[];
  mode: ROOM_MODE_OPTION_VALUE;
  keepWall?: boolean;
  keepFloor?: boolean;
  additionalInfo?: string;
  goodWeatherDaylight?: GoodWeatherDaylightValue;
  goodWeatherSeason?: GoodWeatherSeasonValue;
};

const modesWithRequiredFields = [
  ROOM_MODE_OPTIONS.CHANGE_FLOOR,
  ROOM_MODE_OPTIONS.CHANGE_STYLE,
  ROOM_MODE_OPTIONS.FURNITURE_REMOVAL,
  ROOM_MODE_OPTIONS.FURNISH_EMPTY_ROOM,
];

const renderImage = async ({
  image,
  roomtype,
  style,
  colors = [],
  materials = [],
  mode = 'changeStyle',
  keepFloor,
  keepWall,
  additionalInfo,
  goodWeatherDaylight,
  goodWeatherSeason,
}: RenderImageType) => {
  // @ts-expect-error: TS can't infer mode correctly here
  if (modesWithRequiredFields.includes(mode) && (!roomtype || !style)) {
    throw Error('roomtype and style required for this mode');
  }

  const payload = {
    image,
    roomtype,
    style,
    colors: colors.map((color) => color.value).join(','),
    materials: materials.map((material) => material.value).join(','),
    mode,
    keepFloor,
    keepWall,
    additionalInfo,
    ...(goodWeatherDaylight !== undefined ? { goodWeatherDaylight } : {}),
    ...(goodWeatherSeason !== undefined ? { goodWeatherSeason } : {}),
  } as Parameters<(typeof api)['image-rendering']['post']>[0];

  return extractData(api['image-rendering'].post(payload));
};

export const useRenderImage = ({ mutationConfig }: RenderImageOptions = {}) => {
  return useMutation({
    ...mutationConfig,
    mutationFn: renderImage,
  });
};
